/**
 * Workshop Selector Component
 * Displays current workshop in header and allows switching
 */

import React, { useState } from "react";
import { Building2, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useWorkshop } from "../contexts/WorkshopContext";
import { useTheme } from "../contexts/ThemeContext";
import WorkshopSelectionModal from "./WorkshopSelectionModal";

const WorkshopSelector: React.FC = () => {
  const { selectedWorkshop, workshops, setSelectedWorkshop } = useWorkshop();
  const { isDarkMode } = useTheme();
  const [showModal, setShowModal] = useState(false);

  // Don't show selector if user has no workshops
  if (workshops.length === 0) {
    return null;
  }

  // Don't show selector if user has only one workshop
  if (workshops.length === 1 && selectedWorkshop) {
    return (
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${
          isDarkMode
            ? "bg-phoenix-900/30 text-phoenix-300 border-phoenix-700/50"
            : "bg-phoenix-50 text-phoenix-700 border-phoenix-200"
        }`}
      >
        <Building2 className="w-4 h-4" />
        <span className="text-sm font-medium">{selectedWorkshop.name}</span>
      </div>
    );
  }

  return (
    <>
      {/* Workshop Selector Button */}
      <button
        onClick={() => setShowModal(true)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all group ${
          isDarkMode
            ? "bg-gray-800 hover:bg-gray-700 border-gray-700 hover:border-phoenix-600"
            : "bg-white hover:bg-gray-50 border-gray-300 hover:border-phoenix-400"
        }`}
      >
        <Building2
          className={`w-4 h-4 ${
            isDarkMode
              ? "text-gray-400 group-hover:text-phoenix-400"
              : "text-gray-600 group-hover:text-phoenix-600"
          }`}
        />
        <span
          className={`text-sm font-semibold truncate ${
            isDarkMode
              ? "text-gray-200 group-hover:text-white"
              : "text-gray-900"
          }`}
        >
          {selectedWorkshop ? selectedWorkshop.name : "Select Workshop"}
        </span>
        <ChevronDown
          className={`w-4 h-4 ${
            isDarkMode
              ? "text-gray-500 group-hover:text-phoenix-400"
              : "text-gray-400 group-hover:text-phoenix-600"
          }`}
        />
      </button>

      {/* Workshop Selection Modal */}
      <WorkshopSelectionModal
        workshops={workshops}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSelect={(workshop) => {
          setSelectedWorkshop(workshop);
          setShowModal(false);
        }}
        canClose={true}
      />
    </>
  );
};

export default WorkshopSelector;
